import React, { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import ReportDetailsForm from "components/CaseManagement/CaseForms/ReportDetailsForm";
import makeStyles from "@mui/styles/makeStyles";
import styles from "assets/jss/default/components/newCaseWithDiagnosticDialogStyle";
import { useUpdateEffect } from "react-use";
import cx from "classnames";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Accordion from "@mui/material/Accordion";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionActions from "@mui/material/AccordionActions";
import { StaticSplinePanel } from "components/Panels/SplinePanelSmall";
import * as yup from "yup";
import { useFormAutoValidation } from "hooks/useFormValidation";
import EditableSeverityChip from "components/SeverityChip/EditableSeverityChip";
import { toUtc } from "core/datetime";
import { DatePicker } from "components/DatePicker";
import { NewAttachmentsList } from "components/Attachments/NewAttachmentList";
import { ExistingAttachmentsList } from "components/Attachments/ExistingAttachmentsList";
import { useEventAttachmentFilesQuery } from "hooks/queries/attachments";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";

const useStyles = makeStyles(styles);
const validationSchema = yup.object({
  date_time: yup
    .date()
    .label("Date")
    .required("Required")
});

function ReportForm({
  eventModel,
  severityStatus,
  variant,
  onFormChange,
  update,
  attachmentsToUpload,
  setAttachmentsToUpload,
  attachmentIdsToDelete,
  setAttachmentIdsToDelete,
  filesDropping,
  setFilesDropping
}) {
  const classes = useStyles();
  const [severity, setSeverity] = useState(severityStatus ?? 0);

  const {
    data: attachments,
    isLoading: isAttachmentsLoading,
    isError: isAttachmentsError
  } = useEventAttachmentFilesQuery({
    caseEventId: eventModel.id
  });

  const formStateRef = useRef(update);
  const onReportDetailsFormChanged = useCallback(
    m => {
      formStateRef.current = m.isValid;
      onFormChange({
        eventModel: {
          ...m.model,
          __rawAttachments: eventModel?.__rawAttachments
        },
        severityStatus: severity,
        isValid: m.isValid
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onFormChange, severity]
  );

  const formik = useFormAutoValidation(
    eventModel,
    onReportDetailsFormChanged,
    validationSchema,
    false
  );

  const onChangeDate = useCallback(
    val => {
      formik.setFieldValue("date_time", val);
      onFormChange({
        eventModel: {
          ...eventModel,
          date_time: toUtc(val)
        },
        severityStatus: severity,
        isValid: formStateRef.current
      });
    },
    [formik, eventModel, severity, onFormChange]
  );

  const chartHeight = "320px";

  useUpdateEffect(() => {
    onFormChange({
      eventModel,
      severityStatus: severity,
      attachmentChanges:
        attachmentsToUpload.length > 0 || attachmentIdsToDelete?.length > 0,
      isValid: formStateRef.current
    });
  }, [severity, attachmentsToUpload, attachmentIdsToDelete]);

  const trendChartVisible = eventModel?.params?.trendChartData;

  const getTitle = variant => {
    switch (variant) {
      case "inspection":
        return "Inspection";
      case "diagnosis":
        return "Diagnostic";
      case "repair":
        return "Repair";
      default:
        return "Report";
    }
  };

  return (
    <>
      <Box>
        <Typography variant="h6" component="h4" fontWeight="regular">
          {getTitle(variant)}
          <span className={classes.requiredField}>*</span>
        </Typography>

        <Stack direction="row" spacing={2}>
          <EditableSeverityChip
            severity={severity}
            setSeverity={setSeverity}
            data-qa="CP-Severity-Chip-Report-Severity"
          />
          <Box sx={{ py: "6px" }}>
            <DatePicker
              id="date-picker-dialog"
              label={`${getTitle(variant)} date`}
              required
              date={new Date(formik.values.date_time)}
              onChange={val => {
                onChangeDate(val);
              }}
              disableFuture={true}
              ariaLabel="report date"
              data-qa="CP-Date-Report-Date"
            />
          </Box>
        </Stack>
      </Box>

      <div className={classes.accordionSection}>
        <Divider
          className={cx([classes.dividerWrapper, classes.firstDividerMargin])}
        />
        <Accordion defaultExpanded disableGutters>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="accordion-summary"
            data-qa="CP-Accordion-Report-Summary"
          >
            <Typography variant="h6" component="h4" fontWeight="regular">
              Summary
            </Typography>
          </AccordionSummary>
          <AccordionActions>
            <Grid container direction="row" alignItems="flex-start">
              <Grid item xs={12} md={12}>
                <ReportDetailsForm
                  onFormChange={onReportDetailsFormChanged}
                  eventModel={eventModel}
                />
              </Grid>
            </Grid>
          </AccordionActions>
        </Accordion>
        <Divider className={classes.dividerWrapper} />
        <Accordion defaultExpanded disableGutters>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="accordion-uploaded-files"
            data-qa="CP-Accordion-Report-Uploaded-Files"
          >
            <Typography variant="h6" component="h4" fontWeight="regular">
              New Attachments
            </Typography>
          </AccordionSummary>
          <AccordionActions>
            <NewAttachmentsList
              attachmentsToUpload={attachmentsToUpload}
              setAttachmentsToUpload={setAttachmentsToUpload}
              filesDropping={filesDropping}
              setFilesDropping={setFilesDropping}
            />
          </AccordionActions>
        </Accordion>
        {update &&
          ((attachments && attachments.length > 0) || isAttachmentsLoading) && (
            <>
              <Divider className={classes.dividerWrapper} />
              <Accordion defaultExpanded disableGutters>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="accordion-attachments"
                  data-qa="CP-Accordion-Report-Attachments"
                >
                  <Typography variant="h6" component="h4" fontWeight="regular">
                    Existing Attachments
                  </Typography>
                </AccordionSummary>
                <AccordionActions>
                  <ProgressIndicatorContainer
                    loading={isAttachmentsLoading}
                    error={isAttachmentsError}
                  >
                    <ExistingAttachmentsList
                      initialAttachments={attachments}
                      setAttachmentIdsToDelete={setAttachmentIdsToDelete}
                    />
                  </ProgressIndicatorContainer>
                </AccordionActions>
              </Accordion>
            </>
          )}
        {variant === "diagnosis" && trendChartVisible && (
          <>
            <Divider className={classes.dividerWrapper} />
            <Accordion defaultExpanded disableGutters>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="accordion-trend"
                data-qa="CP-Accordion-Report-Trend"
              >
                <h4 className={classes.accordionHeader}>Trend</h4>
              </AccordionSummary>
              <AccordionActions>
                <Grid container direction="row" alignItems="center">
                  <Grid item xs={12} md={12}>
                    {eventModel?.params?.trendChartData ? (
                      <StaticSplinePanel
                        data={eventModel.params.trendChartData.data}
                        height={chartHeight}
                        chartHeight={chartHeight}
                        legendEnabled={false}
                        unit={eventModel.params.trendChartData.unit}
                      />
                    ) : null}
                  </Grid>
                </Grid>
              </AccordionActions>
            </Accordion>
            <Divider
              className={cx([
                classes.dividerWrapper,
                classes.lastDividerMargin
              ])}
            />
          </>
        )}
      </div>
    </>
  );
}

ReportForm.propTypes = {
  eventModel: PropTypes.object,
  variant: PropTypes.oneOf(["inspection", "diagnosis"]).isRequired,
  onFormChange: PropTypes.func.isRequired,
  severityStatus: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  update: PropTypes.bool,
  attachmentsToUpload: PropTypes.array,
  setAttachmentsToUpload: PropTypes.func,
  attachmentIdsToDelete: PropTypes.array,
  setAttachmentIdsToDelete: PropTypes.func,
  filesDropping: PropTypes.bool,
  setFilesDropping: PropTypes.func
};

ReportForm.defaultProps = {
  severityStatus: null,
  update: false
};

export default ReportForm;
